import React, { useState, forwardRef } from 'react';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ChevronDown } from 'lucide-react';
import { WindScenario } from "../../../services/api";
import { WIND_HEIGHTS, WIND_SCENARIOS } from "../../../utils/windChartConstants";
import { ChartEmptyState } from '../ChartEmptyState';

export interface WindCumulativeBin {
  min: number;
  /** Cumulative installable power for all bins at or above this speed (MW) */
  power_mw: number;
  /** Cumulative annual energy for all bins at or above this speed (GWh) */
  energy_gwh: number;
  /** Capacity factor: suitable area / total area (%) */
  capacity_factor: number;
}

export interface WindCumulativeResponse {
  metadata: {
    layer: string;
    unit: string;
    scenario: string;
    area_km2: number;
  };
  distribution: WindCumulativeBin[];
  total_accumulated_mw: number;
  annual_mean_mw: number;
}


interface WindCumulativeChartProps {
  /** Response from POST /api/v1/report/wind-cumulative */
  data: WindCumulativeResponse | null;
  /** Currently selected measurement height (e.g. "100m"), controlled by parent */
  selectedHeight: string;
  /** Callback to sync height with sibling wind charts */
  onHeightChange?: (height: string) => void;
  /** Currently selected viability scenario */
  selectedScenario: WindScenario;
  /** Callback to sync scenario with WindPotentialChart */
  onScenarioChange?: (scenario: WindScenario) => void;
}

/**
 * WindCumulativeChart Component
 *
 * Displays cumulative wind power potential per wind speed threshold.
 * Each bin represents all areas with wind speed >= that threshold.
 * X axis: wind speed threshold (m/s) — bars are ascending (≥6 is largest)
 * Y axis: cumulative installable power (MW)
 * Tooltip: cumulative power (MW) + capacity factor (%)
 * Footer: annual mean (GWh) + total accumulated (MW)
 *
 * Default shows state-wide data; when a geometry is selected,
 * backend recalculates for the selected area.
 *
 * @param data - WindCumulativeResponse from the API
 * @param selectedHeight - Height controlled by parent (default: "100m")
 * @param onHeightChange - Callback to sync height with sibling charts
 */
export const WindCumulativeChart = forwardRef<HTMLDivElement, WindCumulativeChartProps>(
  ({ data, selectedHeight, onHeightChange, selectedScenario, onScenarioChange }, ref) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [scenarioOpen, setScenarioOpen] = useState(false);

    if (!data) {
      return (
        <ChartEmptyState
          ref={ref}
          message="Sem dados disponíveis para esta seleção."
        />
      );
    }

    if (data.metadata.area_km2 === 0) {
      return (
        <ChartEmptyState
          ref={ref}
          message="Área muito pequena para cálculo de potencial instalável."
        />
      );
    }

    const root = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
    const primaryColor = root?.getPropertyValue('--chart-2').trim() || "#4A90D9";
    const secondaryColor = root?.getPropertyValue('--chart-5').trim() || "#0c2856";
    const textSecondary = root?.getPropertyValue('--color-gray-light').trim() || "#61656d";
    const gridLineColor = root?.getPropertyValue('--color-gray-pale').trim() || "#d9d9d9";
    const axisLineColor = root?.getPropertyValue('--border').trim() || "#d9d9d9";
    const textPrimary = root?.getPropertyValue('--foreground').trim() || "#000000";
    const backgroundColor = root?.getPropertyValue('--background').trim() || "#ffffff";
    const borderColor = root?.getPropertyValue('--border').trim() || "#d9d9d9";

    const distribution = data.distribution;

    // Cumulative bins — x axis shows speed thresholds without ≥ prefix
    const categories = distribution.map(bin => `${bin.min.toFixed(1)}`);

    const chartColor = "#227093";
    const seriesData = distribution.map((bin) => ({
      y: bin.power_mw,
      custom: { capacity_factor: bin.capacity_factor },
      color: chartColor,
    }));

    const annualMean = data.annual_mean_mw;
    const totalAccumulated = data.total_accumulated_mw;

    const options: Highcharts.Options = {
      chart: { type: 'column', height: 300, backgroundColor: 'transparent', marginTop: 40, marginLeft: 60, marginRight: 20, spacingBottom: 5 },
      title: { text: undefined },
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        categories,
        crosshair: true,
        labels: { style: { fontSize: "11px", color: textSecondary } },
        lineColor: axisLineColor,
        tickColor: 'transparent',
        title: {
          text: 'm/s',
          margin: 10,
          style: { fontSize: "11px", color: textSecondary, fontWeight: "normal" }
        }
      },
      yAxis: {
        min: 0,
        title: { text: "MW", margin: 15, style: { fontSize: "11px", color: textSecondary, fontWeight: "normal" } },
        labels: { style: { fontSize: "11px", color: textSecondary } },
        gridLineColor: gridLineColor,
      },
      tooltip: {
        backgroundColor,
        borderColor,
        borderRadius: 10,
        padding: 10,
        shadow: true,
        useHTML: true,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const capacityFactor = (this.point as any).custom?.capacity_factor ?? 0;
          return `
            <div style="font-size: 11px; color: ${textSecondary}; margin-bottom: 6px;">
              Velocidade: ≥ ${this.key} m/s
            </div>
            <div style="font-weight: bold; color: ${textPrimary}; font-size: 12px; margin-bottom: 4px;">
              <span style="color: ${primaryColor};">●</span> ${(this.y ?? 0).toFixed(1)} MW
            </div>
            <div style="color: ${textSecondary}; font-size: 11px;">
              Fator de Capacidade: <strong style="color: ${textPrimary};">${capacityFactor.toFixed(1)}%</strong>
            </div>
          `;
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.05,
          groupPadding: 0.1,
          borderWidth: 0,
          borderRadius: 4,
          states: { hover: { borderWidth: 2, borderColor: secondaryColor } },
        },
      },
      series: [{ type: "column", name: "Potencial Cumulativo", data: seriesData }],
    };

    return (
      <div ref={ref} className="relative pb-4 bg-white rounded-lg shadow-md">
        <div className="absolute top-0 right-0 z-10 flex gap-1">
          {/* Dropdown de cenário */}
          <div className="relative">
            <button
              onClick={() => setScenarioOpen(prev => !prev)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-[12px] font-medium text-[#64748B] hover:text-[#0C2856] transition-colors"
            >
              {WIND_SCENARIOS.find(s => s.value === selectedScenario)?.label}
              <ChevronDown size={14} className={`transition-transform ${scenarioOpen ? "rotate-180" : ""}`} />
            </button>
            {scenarioOpen && (
              <div className="absolute right-0 mt-1 w-[120px] bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                {WIND_SCENARIOS.map(s => (
                  <button key={s.value}
                    onClick={() => { onScenarioChange?.(s.value); setScenarioOpen(false); }}
                    className={`block w-full px-4 py-2 text-left text-[12px] transition-colors
                      ${selectedScenario === s.value ? "bg-gray-50 text-[#0C2856] font-bold" : "text-gray-600 hover:bg-gray-50 hover:text-[#0C2856]"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Dropdown de altura */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(prev => !prev)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-[12px] font-medium text-[#64748B] hover:text-[#0C2856] transition-colors"
            >
              Altura {selectedHeight}
              <ChevronDown size={14} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 mt-1 w-[110px] bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                {WIND_HEIGHTS.map(h => (
                  <button key={h}
                    onClick={() => { onHeightChange?.(h); setDropdownOpen(false); }}
                    className={`block w-full px-4 py-2 text-left text-[12px] transition-colors
                      ${selectedHeight === h ? "bg-gray-50 text-[#0C2856] font-bold" : "text-gray-600 hover:bg-gray-50 hover:text-[#0C2856]"}`}
                  >
                    {h}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="w-full">
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>

        <div className="flex justify-end items-center gap-8 mt-4 pr-4">
          <StatItem label="Média Anual" value={`${annualMean.toFixed(1)} MW`} />
          <StatItem label="Total Acumulado" value={`${totalAccumulated.toFixed(1)} MW`} />
        </div>
      </div>
    );
  }
);

WindCumulativeChart.displayName = 'WindCumulativeChart';

const StatItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col items-center">
    <span className="text-[12px] text-[#0C2856] font-medium mb-1">{label}</span>
    <span className="text-[14px] font-bold text-[#1E293B]">{value}</span>
  </div>
);
